/*
 * Copyright (c) 2023 Anvilor Solutions LLC - All Rights Reserved.
 *
 * The information and source code contained herein is the exclusive property of
 * Anvilor Solutions LLC and may not be disclosed, examined, or reproduced in whole
 * or in part without the explicit written authorization from Anvilor Solutions LLC.
 */

import { AnvilorFormsAppStore } from "../anvilor-forms-app-store.service";
import { AnvilorFormsAppStoreContext } from "../anvilor-forms-app-store.service";
import { AnvilorFormsApiService } from "../../services/anvilor-forms-api.service";
import { QueryParamService } from "@anvilor/ngx-apps-core";
import { AnvilorNotificationsService } from "@anvilor/ngx-apps-core";
import { map } from "rxjs/operators";
import { take } from "rxjs/operators";
import { tap } from "rxjs/operators";
import { switchMap } from "rxjs/operators";
import { iif } from "rxjs";
import { of } from "rxjs";
import { catchError } from "rxjs";
import { Observable } from "rxjs";
import { EMPTY } from "rxjs";
import { ResponseEditConfig } from "../../models/submission-edit";
import { ResponseData } from "../../models/submission-edit";
import { AuthenticatedForResponseData } from "../../models/submission-edit";
import { HttpErrorResponse } from "@angular/common/http";
import { AppAuthenticationService } from "../auth/app-authentication";
import { DEFAULT_AUTH_STRATEGY } from "../auth/app-authentication";
import { EDIT_KEY_QPARAM } from "../../consts";
import { pass } from "@anvilor/ngx-core-utils";

export class SubmissionDataLoader {
  private anvilorFormsApi: AnvilorFormsApiService;
  private qparams: QueryParamService;
  private store: AnvilorFormsAppStore;
  private auth: AppAuthenticationService;
  private notify: AnvilorNotificationsService;

  constructor(ctx: AnvilorFormsAppStoreContext) {
    this.anvilorFormsApi = ctx.formsApi;
    this.qparams = ctx.qparams;
    this.store = ctx.store;
    this.auth = ctx.auth;
    this.notify = ctx.notify;
  }

  loadSubmissionData(): Observable<ResponseData | null> {
    const loadSubmissionData$ = (editKey: string) =>
      this.anvilorFormsApi.getEditConfig(editKey).pipe(
        tap((editConfig) => this.onEditConfigLoaded(editConfig)),
        catchError((e) => this.onEditConfigLoadError(e)),
        switchMap((editConfig) => this.auth.authenticateAndGetData(editConfig)),
        tap((data) => this.onSubmissionDataRetrieved(data)),
        map(({ response_data }) => response_data)
      );

    return this.qparams.queryParams$.pipe(
      tap((qp) => {
        const hasLinkId = qp.get("link_id");
        if (hasLinkId) {
          const url = window.location.href.replace("link_id=", "edit_key=");
          window.location.replace(url);
        }
      }),
      map((qp) => qp.get(EDIT_KEY_QPARAM)),
      take(1),
      tap((editKey) => (!!editKey ? this.onEditKeyFound() : this.onNoEditKey())),
      switchMap((editKey) =>
        iif(() => !!editKey, loadSubmissionData$(editKey!), of(null))
      )
    );
  }

  private onEditKeyFound() {
    this.store.patchState({ edit_config_load_status: "active" });
  }

  private onNoEditKey() {
    this.store.patchState({
      edit_config_load_status: "complete",
      response_data_load_status: "complete",
      auth_load_status: "complete",
    });
  }

  private onEditConfigLoaded(editConfig: ResponseEditConfig): void {
    this.checkEditConfigAgainstSandboxMode(editConfig);
    const authStrat = editConfig.auth_strategy ?? DEFAULT_AUTH_STRATEGY;
    this.store.patchState({
      edit_config_load_status: "complete",
      edit_config: {
        edit_key: editConfig.edit_key,
        response_oid: editConfig.response_oid,
        anvilor_form_oid: editConfig.anvilor_form_oid,
        deployment_key: editConfig.deployment_key,
        auth_strategy: authStrat,
      },
      auth_load_status: "active",
    });
  }

  private onSubmissionDataRetrieved(submissionData: AuthenticatedForResponseData) {
    this.store.patchState({
      edit_config_load_status: "complete",
      response_data_load_status: "complete",
      auth_load_status: "complete",
      response_client_data: submissionData.response_data.client_data,
      submission_post_auth_header: submissionData.submission_auth_header,
    });
  }

  private checkEditConfigAgainstSandboxMode(editConfig: ResponseEditConfig): void {
    this.store.subscribeTo(
      this.store.envKey$.pipe(
        tap((k) => {
          if (k !== (editConfig.deployment_key ?? "live")) {
            this.notify.error.active(
              {
                title: "Edit-Key Mismatch!",
                error_code: "EDIT_KEY_ENVIRONMENT_MISMATCH",
                body: `It appears this link has an edit_key for edditing a
              ${editConfig.deployment_key} response against
              the ${k} Anvilor Form,
              which is not allowed.`,
              },
              { disableClose: true }
            );
          }
        })
      )
    );
  }

  private onEditConfigLoadError(_: HttpErrorResponse): Observable<never> {
    this.notify.dialog.open({
      disableClose: true,
      data: {
        title: "Invalid Link",
        body:
          "This is not a valid submission link. Please contact support for " +
          "assistance.",
        actionButtons: [],
      },
    });
    console.error("EDIT_KEY_NOT_FOUND");
    this.store.patchState({ edit_config_load_status: "complete" });
    return EMPTY;
  }
}
